import { queryOptions, useQuery } from '@tanstack/react-query';
import { VolumeEstimationDTO, PredictionsService } from '../generated';
import { AttributeChipData } from '../../components/Prediction/types';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { createElement } from 'react';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const defaultVolumeEstimationConfig: VolumeEstimationConfig = { decimalPlaces: 2 };

export interface VolumeEstimationOptionsProps {
  jobsiteId: string | undefined;
  imageId: string | undefined;
  config?: VolumeEstimationConfig;
}

export function volumeEstimationOptions({
  jobsiteId,
  imageId,
  config = defaultVolumeEstimationConfig,
}: VolumeEstimationOptionsProps) {
  const transformFn = (resp: VolumeEstimationDTO) => transformVolumeEstimation(resp, config);

  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: ['volumeEstimation', jobsiteId, imageId, config],
    queryFn: async () => {
      if (!jobsiteId || !imageId) {
        throw new Error('jobsiteId and imageId are required');
      }

      const response = await PredictionsService.predictionControllerGetVolumeEstimationByImageId({
        path: { jobsiteId, imageId },
      });

      if (response.data !== undefined) {
        return transformFn(response.data);
      } else {
        throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!imageId,
  });
}

export function useVolumeEstimation(props: VolumeEstimationOptionsProps) {
  return useQuery(volumeEstimationOptions(props));
}

export interface VolumeEstimationConfig {
  decimalPlaces?: number;
}

export function transformVolumeEstimation(
  data: VolumeEstimationDTO,
  config: VolumeEstimationConfig
): AttributeChipData[] {
  if (
    !Number.isFinite(data.empty_volume) ||
    !Number.isFinite(data.batch_volume) ||
    data.empty_volume <= 0 ||
    data.batch_volume <= 0 ||
    data.empty_volume < data.batch_volume // prevent negative volumes, empty volume must be greater than batch volume
  ) {
    return [
      {
        id: 'volume-estimation',
        name: 'attributes.volume.displayName',
        value: '-',
        tooltipText: 'attributes.volume.invalidTooltip',
        icon: createElement(LocalShippingIcon, { sx: { flexShrink: 0, marginLeft: 0.5 } }),
        status: 'OK',
        probability: undefined,
      },
    ];
  }

  const currentVolume = data.empty_volume - data.batch_volume;

  // Format with specified decimal places
  const decimalPlaces = config.decimalPlaces ?? 2;
  const volumeText = `${currentVolume.toFixed(decimalPlaces)} m³`;

  return [
    {
      id: 'volume-estimation',
      name: 'attributes.volume.displayName',
      value: volumeText,
      tooltipText: 'attributes.volume.tooltip',
      icon: createElement(LocalShippingIcon, { sx: { flexShrink: 0, marginLeft: 0.5 } }),
      status: 'OK',
      probability: undefined,
    },
  ];
}
